<script setup>
import { computed } from 'vue'

import {
    ExclamationTriangleIcon,
    LockClosedIcon,
    MagnifyingGlassIcon,
    WrenchScrewdriverIcon,
} from '@heroicons/vue/24/outline'

import BrandButton from '@/components/ui/BrandButton.vue'

const props = defineProps({
    status: Number,
    message: {
        type: String,
        default: null,
    },
})

const title = computed(
    () =>
        ({
            503: 'Služba je dočasně nedostupná',
            500: 'Něco se pokazilo',
            404: 'Stránku jsme nenašli',
            403: 'Tady to pro vás není',
        })[props.status] ?? 'Stalo se něco neočekávaného'
)

const description = computed(
    () =>
        ({
            503: 'Právě provádíme údržbu, abychom vám mohli nabídnout lepší službu. Zkuste to prosím za chvíli.',
            500: 'Něco se na naší straně pokazilo. Zkuste to prosím znovu, nebo nám napište.',
            404: 'Adresa, kterou hledáte, neexistuje nebo byla přesunuta. Možná vám pomůže návrat na hlavní stránku.',
            403: 'K této části webu nemáte oprávnění. Pokud si myslíte, že jde o chybu, ozvěte se nám.',
        })[props.status] ?? 'Zkuste to prosím znovu nebo nás kontaktujte.'
)

const iconComponent = computed(
    () =>
        ({
            503: WrenchScrewdriverIcon,
            500: ExclamationTriangleIcon,
            404: MagnifyingGlassIcon,
            403: LockClosedIcon,
        })[props.status] ?? ExclamationTriangleIcon
)
</script>

<template>
    <Head :title="`${status} — ${title}`" />

    <div class="flex min-h-screen items-center justify-center bg-white px-6 py-12">
        <div class="w-full max-w-2xl text-center">
            <component
                class="text-brand-100 mx-auto size-20"
                :is="iconComponent"
                stroke-width="1.5"
                aria-hidden="true"
            />

            <p class="text-brand-300 mt-8 text-sm font-semibold tracking-[0.18em] uppercase">Chyba {{ status }}</p>

            <h1
                class="text-brand-900 mt-4 text-4xl leading-[1.1] font-extrabold tracking-tight sm:text-5xl md:text-6xl"
            >
                {{ title }}
            </h1>

            <p class="mx-auto mt-6 max-w-lg text-lg leading-relaxed text-zinc-600">
                {{ description }}
            </p>

            <div
                class="mx-auto mt-10 max-w-lg rounded-xl bg-zinc-50 p-5 text-left"
                v-if="message"
            >
                <p class="text-xs font-semibold tracking-wider text-zinc-500 uppercase">Detail chyby</p>

                <pre class="text-brand-900 mt-2 max-h-40 overflow-auto text-sm whitespace-pre-wrap">{{ message }}</pre>
            </div>

            <div class="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
                <BrandButton href="/">Zpět na hlavní stránku</BrandButton>

                <BrandButton
                    variant="secondary"
                    href="/#contact"
                >
                    Napsat nám
                </BrandButton>
            </div>
        </div>
    </div>
</template>
